import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";
import PaymentMethodsList from "./PaymentMethodsList";
import CardPaymentMethodForm from "./CardPaymentMethodForm";
import BankAccountPaymentMethodForm from "./BankAccountPaymentMethodForm";
import PayPalPaymentMethodForm from "./PayPalPaymentMethodForm";
import { Button } from "../ui/Button";
import { CreditCard, Building2, Loader2, DollarSign } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { RoleEnum } from "../../modules";
import { useTranslation } from "react-i18next";

const PaymentMethodsManager: React.FC<{ availableBalance?: number }> = ({ availableBalance }) => {
  const { t } = useTranslation();
  const { loading, error, paymentMethods } = usePaymentMethods();
  const { user } = useSelector((state: RootState) => state.auth);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [showAddPayPal, setShowAddPayPal] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    addCard: false,
    addBankAccount: false,
    addPayPal: false
  });


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">{t("paymentMethodsManager.loadingPaymentMethods")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t("paymentMethodsManager.error")}</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Add Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("paymentMethodsManager.paymentMethods")}</h3>
          <p className="text-sm text-gray-600">{t("paymentMethodsManager.managePaymentMethods")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowAddCard(true)}
            disabled={actionLoading.addCard}
            className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading.addCard ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            {actionLoading.addCard ? t("paymentMethodsManager.processing") : t("paymentMethodsManager.addCard")}
          </Button>
          
          {/* Hide PayPal button for clients */}
          {/* {Number(user?.role?.id) !== RoleEnum.client && (
            <Button
              onClick={() => setShowAddPayPal(true)}
              disabled={actionLoading.addPayPal}
              variant="outline"
              className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading.addPayPal ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DollarSign className="h-4 w-4" />
              )}
              {actionLoading.addPayPal ? "Processing..." : "Add PayPal"}
            </Button>
          )} */}

          {/* <Button
            onClick={() => setShowAddBankAccount(true)}
            disabled={actionLoading.addBankAccount}
            variant="outline"
            className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading.addBankAccount ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            {actionLoading.addBankAccount ? "Processing..." : "Add Bank Account"}
          </Button> */}
        </div>
      </div>

      {/* Payment Methods List */}
      <PaymentMethodsList />

      {/* Modals */}
      <AnimatePresence>
        {showAddCard && (
          <CardPaymentMethodForm 
            onClose={() => {
              setShowAddCard(false);
              setActionLoading(prev => ({ ...prev, addCard: false }));
            }}
            onSuccess={() => {
              setActionLoading(prev => ({ ...prev, addCard: true }));
              setShowAddCard(false);
              // Reset loading after a short delay to show success state
              setTimeout(() => {
                setActionLoading(prev => ({ ...prev, addCard: false }));
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddPayPal && (
          <PayPalPaymentMethodForm 
            onClose={() => {
              setShowAddPayPal(false);
              setActionLoading(prev => ({ ...prev, addPayPal: false }));
            }}
            onSuccess={() => {
              setActionLoading(prev => ({ ...prev, addPayPal: true }));
              setShowAddPayPal(false);
              // Reset loading after a short delay to show success state
              setTimeout(() => {
                setActionLoading(prev => ({ ...prev, addPayPal: false }));
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddBankAccount && (
          <BankAccountPaymentMethodForm 
            onClose={() => {
              setShowAddBankAccount(false);
              setActionLoading(prev => ({ ...prev, addBankAccount: false }));
            }}
            onSuccess={(response) => {
              setActionLoading(prev => ({ ...prev, addBankAccount: true }));
              setShowAddBankAccount(false);
              
              // Handle verification requirements
              if (response?.requiresVerification) {
                // Show verification message or redirect to verification
                console.log("Bank account requires verification:", response.verificationDetails);
                // You can add a toast notification or modal here
                alert("Bank account added successfully! Please complete verification to use this payment method.");
              } else {
                console.log("Bank account added successfully");
              }
              
              // Reset loading after a short delay to show success state
              setTimeout(() => {
                setActionLoading(prev => ({ ...prev, addBankAccount: false }));
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentMethodsManager;